export type OrderStatus = 'pending' | 'paid' | 'failed' | 'cancelled' | 'done';
export type PaymentProvider = 'delivery' | 'bank' | 'wishmoney';
export type PaymentStatus = 'authorized' | 'paid' | 'failed' | 'refunded';

/** Every status the API accepts, in the order the admin picker offers them. */
export const ORDER_STATUSES: OrderStatus[] = [
    'pending',
    'paid',
    'failed',
    'cancelled',
    'done',
];

/**
 * One line of an order. The API snapshots `productName` and the price actually
 * charged at checkout, so a line stays readable after the product is edited —
 * or deleted, which leaves `productId` null rather than removing the line.
 */
export interface AdminOrderItem {
    id: number;
    orderId: number;
    /** null once the product has been deleted from the catalogue. */
    productId: number | null;
    productName: string;
    quantity: number;
    /** Per-unit price charged, after any discount. */
    unitPrice: number;
    /** unitPrice * quantity. */
    lineTotal: number;
}

/**
 * One row of GET /api/orders (admin only). The API joins orders with their
 * address and payment and nests the line items, so a row carries the whole
 * order.
 */
export interface AdminOrder {
    id: number;
    /** Empty for a guest checkout. */
    customerEmail: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    status: OrderStatus;
    createdAt: string;
    /** Total before any discount. */
    subtotal: number;
    /** What the customer actually pays. Reconciles with the sum of line totals. */
    grandTotal: number;

    city: string;
    state: string;
    addressDescription: string;

    paymentProvider: PaymentProvider | '';
    paymentId: string;
    paymentStatus: PaymentStatus | '';
    paymentAmount: number;

    items: AdminOrderItem[];
    /** Sum of the item quantities. */
    totalQuantity: number;
}

/**
 * Query for GET /api/orders. All optional and combinable, but `offset` is only
 * accepted alongside `limit` — the API answers 400 otherwise. Omitting `limit`
 * returns every order.
 */
export interface OrderListParams {
    status?: OrderStatus;
    limit?: number;
    offset?: number;
}
